import React, { useState } from 'react';
import { AlertTriangleIcon, CheckIcon, MapPinIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Field, Select, Textarea } from '../../components/ui/Field';
import { useNetwork } from '../../contexts/NetworkContext';
import { IS_DEMO_DATA } from '../../data/network';
import { naira } from '../../utils/format';
import { applicationTone, documentTone, balancesFor, earningTone, repTone, scoreFor, taskTone, VERIFICATION_RADIUS_M } from '../../utils/network';
import { RANK_ORDER } from '../../types/network';
import type { FeatureFlagKey, RepRank, VerificationDecision } from '../../types/network';
const TABS = ['Network', 'Applications', 'Review queue', 'Finance', 'Performance', 'Safety and fraud', 'Intelligence', 'Configuration', 'Audit'] as const;
type Tab = (typeof TABS)[number];
const ACTOR = 'Ops — K. Yusuf';
const REVIEWER = 'Reviewer — D. Aliu';
const FINANCE = 'Finance — R. Okoro';
export function NetworkControl() {
  const network = useNetwork();
  const [tab, setTab] = useState<Tab>('Network');
  const [approveId, setApproveId] = useState<string | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const pendingApplications = network.applications.filter(application => ['Submitted', 'Under review', 'Shortlisted', 'Interview', 'Waitlisted'].includes(application.status));
  const queue = network.verifications.filter(submission => ['Submitted', 'Under review'].includes(submission.status));
  const pendingEarnings = network.earnings.filter(entry => entry.status === 'Pending');
  const openFraud = network.fraud.filter(flag => flag.state === 'Awaiting review');
  return <div className="space-y-6" data-id="element-6223" data-mp="2hmb4sl3ccydq">
      <PageHeader eyebrow="Campuzon administration" title="Campus Network control centre" description="Campuses, applications, field review, earnings, performance, safety and configuration. Operations, review, finance and risk are separated, and every action is attributed." actions={IS_DEMO_DATA ? <Badge tone="warn" icon={<AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-6226" data-mp="23ocqtgsxn3yr" />} data-id="element-6225" data-mp="1ywbk0aeja5g4">
              Development data — not production metrics
            </Badge> : undefined} data-id="element-6224" data-mp="2mnqp2axp30v2" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Network administration sections" data-id="element-6227" data-mp="1p19tpb8iuxby" />

      {/* -------------------------------------------------------- network -- */}
      {tab === 'Network' && <div className="space-y-4" data-id="element-6228" data-mp="1g0v4w1wcn03p">
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-6229" data-mp="1kyd5n507q90m">
            {[{
          label: 'Active representatives',
          value: network.reps.filter(rep => rep.status === 'Active').length,
          note: `${network.reps.filter(rep => rep.status === 'Pending onboarding').length} onboarding`
        }, {
          label: 'Published campuses',
          value: network.campuses.filter(campus => campus.published).length,
          note: `${network.campuses.length} configured`
        }, {
          label: 'Awaiting review',
          value: queue.length,
          note: 'Field submissions'
        }, {
          label: 'Open applications',
          value: pendingApplications.length,
          note: 'In the pipeline'
        }].map(metric => <li key={metric.label} className="rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-6230" data-mp="231otcxqom49f">
                <p className="text-[12px] text-ink-muted" data-id="element-6231" data-mp="1y099h908q3sm">{metric.label}</p>
                <p className="cz-num mt-1 text-2xl font-semibold tracking-tight text-ink" data-id="element-6232" data-mp="3shrcm7mz6mqe">
                  {metric.value}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-6233" data-mp="62y90pks54fa">{metric.note}</p>
              </li>)}
          </ul>

          <Panel title="Campuses" description="A new campus is configured here, not in code. Publishing opens it to applicants and students." data-id="element-6234" data-mp="a4tc9zm9trvp">
            <ul className="space-y-3" data-id="element-6235" data-mp="3eihyrem8s4pl">
              {network.campuses.map(campus => {
            const reps = network.reps.filter(rep => rep.campusId === campus.id);
            const lead = network.reps.find(rep => rep.id === campus.campusLeadRepId);
            const ready = campus.areas.length > 0 && lead !== undefined;
            return <li key={campus.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-6236" data-mp="xw8k27wth1ec">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-6237" data-mp="1cx5uad839t3o">
                      <p className="text-[14px] font-semibold text-ink" data-id="element-6238" data-mp="1cwdwqwuxllar">{campus.name}</p>
                      <Badge tone={campus.published ? 'ok' : 'neutral'} data-id="element-6239" data-mp="17ym7kalo8da9">
                        {campus.published ? 'Published' : 'Unpublished'}
                      </Badge>
                      <Badge tone={campus.active ? 'brand' : 'neutral'} data-id="element-6240" data-mp="z4ppb6vmnl04">
                        Intake: {campus.recruitmentStatus}
                      </Badge>
                      <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6241" data-mp="p7l00y6hj9f3">
                        {campus.studentEstimate.toLocaleString('en-NG')} students
                      </span>
                    </div>
                    <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-6242" data-mp="g7esdsxgx3nu">
                      {campus.institution} · {campus.city}, {campus.state}, {campus.country} ·{' '}
                      {campus.areas.length} area{campus.areas.length === 1 ? '' : 's'} · {reps.length}{' '}
                      representative{reps.length === 1 ? '' : 's'}
                    </p>
                    <p className="mt-1 text-[12px] text-ink-subtle" data-id="element-6243" data-mp="7wftbw3pvomv">
                      Campus Lead: {lead?.name ?? 'not appointed'}
                    </p>
                    {campus.areas.length > 0 && <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-6244" data-mp="2ywdirrdf3st">
                        {campus.areas.map(area => <li key={area.id} data-id="element-6245" data-mp="qoibxczx5ezr">
                            <Badge tone="neutral" icon={<MapPinIcon className="h-3.5 w-3.5" data-id="element-6247" data-mp="vnl5o1ag7ysf" />} data-id="element-6246" data-mp="qmno9qx9j8rz">
                              {area.name}
                            </Badge>
                          </li>)}
                      </ul>}
                    {!campus.published && <div className="mt-2.5" data-id="element-6248" data-mp="32eaxfb3afdr2">
                        <Button size="sm" variant="primary" disabled={!ready} onClick={() => network.publishCampus(campus.id, ACTOR)} data-id="element-6249" data-mp="32dhy2v6nrqiy">
                          {ready ? 'Publish campus' : 'Needs areas and a campus lead first'}
                        </Button>
                      </div>}
                  </li>;
          })}
            </ul>
          </Panel>

          <Panel title="Representatives" data-id="element-6250" data-mp="33tdijuyrqwzq">
            <ul className="divide-y divide-line" data-id="element-6251" data-mp="2g5cdwx4po0pl">
              {network.reps.map(rep => {
            const score = scoreFor(network.events.filter(item => item.repId === rep.id), network.verifications.filter(item => item.repId === rep.id), network.tasks.filter(item => item.assignedRepId === rep.id)).score;
            return <li key={rep.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-6252" data-mp="1xe35ysal9k04">
                    <span className="min-w-0 flex-1" data-id="element-6253" data-mp="1sfi4kpjloua3">
                      <span className="block text-[13px] font-medium text-ink" data-id="element-6254" data-mp="16wzxi0fdhe5k">{rep.name}</span>
                      <span className="cz-num block text-[12px] text-ink-muted" data-id="element-6255" data-mp="1bw325jrfxid0">
                        {rep.repCode} · {rep.rank} ·{' '}
                        {network.campuses.find(campus => campus.id === rep.campusId)?.name ?? '—'}
                      </span>
                    </span>
                    <Badge tone={repTone(rep.status)} data-id="element-6256" data-mp="1pvuab3va04j3">{rep.status}</Badge>
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6257" data-mp="1pvkkphe57zdx">{score}</span>
                    {rep.status === 'Active' && <Button size="sm" variant="secondary" onClick={() => network.setRepStatus(rep.id, 'Suspended', 'Suspended pending review', ACTOR)} data-id="element-6258" data-mp="1zl436lxy1r89">
                        Suspend
                      </Button>}
                    {rep.status === 'Suspended' && <Button size="sm" variant="secondary" onClick={() => network.setRepStatus(rep.id, 'Active', 'Reinstated after review', ACTOR)} data-id="element-6259" data-mp="1zmrnchfbb9m">
                        Reinstate
                      </Button>}
                  </li>;
          })}
            </ul>
            <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-6260" data-mp="gn7vj8m4heil">
              Suspension and termination remove operational access but preserve every historical record,
              including verifications attributed to that representative.
            </p>
          </Panel>
        </div>}

      {/* --------------------------------------------------- applications -- */}
      {tab === 'Applications' && <div className="space-y-3" data-id="element-6261" data-mp="zksj8cd5i71m">
          {network.applications.length === 0 ? <EmptyState title="No applications yet" description="Applications arrive from the public programme page." data-id="element-6262" data-mp="27qldzlxuzdny" /> : network.applications.map(application => <div key={application.id} className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-6263" data-mp="2hpu3a1hs443z">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6264" data-mp="2cpy8msuz1p13">
                  <p className="text-[14px] font-semibold text-ink" data-id="element-6265" data-mp="2qpzaobmz32i5">{application.fullName}</p>
                  <Badge tone={applicationTone(application.status)} data-id="element-6266" data-mp="2qnmjd7vbrfsf">{application.status}</Badge>
                  <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6267" data-mp="2lp1rpwu524fl">
                    {application.id}
                    {application.submittedOn ? ` · ${application.submittedOn}` : ''}
                  </span>
                </div>
                <dl className="mt-2 grid gap-x-6 gap-y-1.5 sm:grid-cols-2" data-id="element-6268" data-mp="39hic4e1cm8i2">
                  {[{
            term: 'Campus',
            value: `${network.campuses.find(campus => campus.id === application.campusId)?.institution ?? '—'}${application.campusStatusAtApplication ? ` — ${application.campusStatusAtApplication} at application` : ''}`
          }, {
            term: 'Status',
            value: application.applicantStatus
          }, {
            term: 'Programme',
            value: `${application.programme} · ${application.level} · graduates ${application.graduationYear}`
          }, {
            term: 'Lives in',
            value: `${application.location.area}, ${application.location.city}, ${application.location.state}`
          }, {
            term: 'Gets around by',
            value: application.transport
          }, {
            term: 'Availability',
            value: `${application.weeklyHours} · ${application.availableDays.join(', ') || 'no days given'}${application.fieldPeriods.length > 0 ? ` · ${application.fieldPeriods.join(', ')}` : ''}`
          }, {
            term: 'Connectivity',
            value: application.connectivity
          }, {
            term: 'Equipment',
            value: `${application.hasSmartphone ? 'Smartphone' : 'No smartphone'}, ${application.hasCamera ? 'camera works' : 'camera unsuitable'}`
          }, {
            term: 'Contact',
            value: `${application.email} · ${application.phone}`
          }, {
            term: 'Interested in',
            value: application.preferredActivities.join(', ') || '—'
          }].map(row => <div key={row.term} data-id="element-6269" data-mp="11v9lhm3ecdzx">
                      <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6270" data-mp="3rktnbrkr858e">
                        {row.term}
                      </dt>
                      <dd className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-6271" data-mp="3mo37n5cv3bni">{row.value}</dd>
                    </div>)}
                </dl>
                <p className="mt-2 text-[13px] leading-6 text-ink-muted" data-id="element-6272" data-mp="3hgd4qqfw4a0w">{application.motivation}</p>
                <ul className="mt-2.5 space-y-2" data-id="element-6273" data-mp="3mf80su2vbv2y">
                  {application.documents.map(document => <li key={document.key} className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-canvas px-3.5 py-2.5" data-id="element-6274" data-mp="3r7rctmlwqi5n">
                      <span className="min-w-0 flex-1" data-id="element-6275" data-mp="40p0ui0zsr3j">
                        <span className="block text-[13px] font-medium text-ink" data-id="element-6276" data-mp="90tk2ftabzxg">{document.label}</span>
                        <span className="cz-num block text-[12px] text-ink-muted" data-id="element-6277" data-mp="e0zbbuvn7kf0">
                          {document.fileName ? `${document.fileName} · ${document.mimeType ?? 'unknown type'}` : 'Not provided'}
                        </span>
                        {document.reviewNote && <span className="block text-[12px] leading-5 text-warn-700" data-id="element-6278" data-mp="a5mwn9xrgkz8">
                            Returned: {document.reviewNote}
                          </span>}
                      </span>
                      <Badge tone={documentTone(document.state)} data-id="element-6279" data-mp="e5djx3o2wiz0">{document.state}</Badge>
                      {['Submitted', 'Under review'].includes(document.state) && <>
                          <Button size="sm" variant="secondary" onClick={() => network.reviewApplicationDocument(application.id, document.key, 'Verified', null, ACTOR)} data-id="element-6280" data-mp="3ijlnxy9sfi6e">
                            Verify
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => network.reviewApplicationDocument(application.id, document.key, 'Re-upload required', 'Not fully legible. Please send the whole document.', ACTOR)} data-id="element-6281" data-mp="1j4k7c1y6iqhq">
                            Return
                          </Button>
                        </>}
                    </li>)}
                </ul>
                <ul className="mt-2 flex flex-wrap gap-1.5" data-id="element-6282" data-mp="cqb3uy7gfpx7">
                  {application.consents.map(consent => <li key={consent.key} data-id="element-6283" data-mp="3tq2ud3mw0s9o">
                      <Badge tone={consent.given ? 'ok' : 'warn'} data-id="element-6284" data-mp="3tpt6bllekv6m">
                        {consent.label.split('.')[0]}
                        {consent.given ? ' ✓' : ' — not given'}
                      </Badge>
                    </li>)}
                </ul>
                {application.internalNotes.length > 0 && <ul className="mt-2 space-y-1 border-t border-line pt-2" data-id="element-6285" data-mp="3aztmpe8c08al">
                    {application.internalNotes.map(note => <li key={note.at + note.body} className="text-[12px] leading-5 text-ink-muted" data-id="element-6286" data-mp="35zp0r8onmlcz">
                        {note.at} · {note.actor}: {note.body}
                      </li>)}
                  </ul>}
                {application.decisionReason && <p className="mt-2 text-[12px] leading-5 text-ink" data-id="element-6287" data-mp="106gzzuniysaj">
                    Decision reason: {application.decisionReason}
                  </p>}

                {!['Approved', 'Rejected', 'Withdrawn'].includes(application.status) && <div className="mt-3 flex flex-wrap gap-2" data-id="element-6288" data-mp="17qrl7ukvfhid">
                    <Button size="sm" variant="secondary" onClick={() => network.moveApplication(application.id, 'Under review', 'Picked up by operations')} data-id="element-6289" data-mp="17p82lxbqsudf">
                      Under review
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => network.moveApplication(application.id, 'Shortlisted', 'Fits an uncovered area')} data-id="element-6290" data-mp="jvoxjpxszk3h">
                      Shortlist
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => network.moveApplication(application.id, 'Interview', 'Interview scheduled')} data-id="element-6291" data-mp="xqsfe5fu6597">
                      Interview
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => network.addApplicationNote(application.id, 'Asked the applicant for the missing document.')} data-id="element-6292" data-mp="2v7hn2918i5al">
                      Request information
                    </Button>
                    <Button size="sm" variant="primary" disabled={application.documents.some(document => document.required && document.state !== 'Verified')} onClick={() => setApproveId(application.id)} data-id="element-6293" data-mp="3dyh32kqr44rq">
                      {application.documents.some(document => document.required && document.state !== 'Verified') ? 'Verify identity documents first' : 'Approve and assign'}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => network.moveApplication(application.id, 'Rejected', 'Does not meet the equipment requirement')} data-id="element-6294" data-mp="92pjl25uci4h">
                      Reject
                    </Button>
                  </div>}
              </div>)}
        </div>}

      {/* --------------------------------------------------- review queue -- */}
      {tab === 'Review queue' && <div className="space-y-3" data-id="element-6295" data-mp="3cimrf4gj5dzn">
          {queue.length === 0 ? <EmptyState title="Nothing awaiting review" description="Field submissions land here the moment they sync." data-id="element-6296" data-mp="3ckzhk4wfyuns" /> : queue.map(submission => {
        const task = network.tasks.find(item => item.id === submission.taskId);
        const rep = network.reps.find(item => item.id === submission.repId);
        const priorByRep = network.verifications.filter(item => item.repId === submission.repId && item.id !== submission.id);
        return <div key={submission.id} className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-6297" data-mp="29as88pb38pij">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-6298" data-mp="2x0cvvli6say5">
                    <p className="text-[14px] font-semibold text-ink" data-id="element-6299" data-mp="3210h1qjqhxkg">{submission.propertyRef}</p>
                    <Badge tone={taskTone(task?.status ?? 'Under review')} data-id="element-6300" data-mp="2o198i5bzl9du">{submission.status}</Badge>
                    {submission.syncState !== 'Synced' && <Badge tone="warn" data-id="element-6301" data-mp="1cm2p6vnq5quz">{submission.syncState}</Badge>}
                    <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6302" data-mp="1hlf8x7jauhtc">
                      {submission.id} · {submission.submittedOn}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-6303" data-mp="153cxci0yiejf">
                    {task?.type ?? 'Verification'} · {task?.title} · submitted by {rep?.name} (
                    {rep?.repCode}) · {priorByRep.length} prior submission(s) on record
                  </p>

                  <div className="mt-2.5 grid gap-3 sm:grid-cols-2" data-id="element-6304" data-mp="1e1yf4ayltpj5">
                    <div className="rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-6305" data-mp="qeq89olj28f0">
                      <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6306" data-mp="lgexnbnertvq">
                        Location
                      </p>
                      {submission.evidence.gps ? <>
                          <p className="cz-num mt-1 text-[12px] text-ink" data-id="element-6307" data-mp="7npcf9fpeszo">
                            {submission.evidence.gps.lat}, {submission.evidence.gps.lng} · ±
                            {submission.evidence.gps.accuracyM}m
                          </p>
                          <p className={`mt-0.5 text-[12px] ${(submission.distanceFromExpectedM ?? 0) > VERIFICATION_RADIUS_M ? 'text-warn-700' : 'text-ink-muted'}`} data-id="element-6308" data-mp="22qc96nmt9f9t">
                            {submission.distanceFromExpectedM ?? '—'}m from the expected location
                          </p>
                        </> : <p className="mt-1 text-[12px] text-warn-700" data-id="element-6309" data-mp="1osxf35lm4jbi">Not captured</p>}
                    </div>
                    <div className="rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-6310" data-mp="1xsaibgj58bns">
                      <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6311" data-mp="348cq3sx0dkd5">
                        Recorded values
                      </p>
                      <ul className="mt-1 space-y-0.5" data-id="element-6312" data-mp="398qyxd6foyc8">
                        {Object.entries(submission.evidence.fields).map(([key, value]) => <li key={key} className="text-[12px] leading-5 text-ink" data-id="element-6313" data-mp="3ect5ymahn1gy">
                            {key}: {value}
                          </li>)}
                        {Object.keys(submission.evidence.fields).length === 0 && <li className="text-[12px] text-ink-muted" data-id="element-6314" data-mp="30k3j6fn4xsis">None recorded</li>}
                      </ul>
                    </div>
                  </div>

                  {submission.evidence.photos.length > 0 && <ul className="mt-2.5 flex flex-wrap gap-2" data-id="element-6315" data-mp="2qmpbb4fa2c23">
                      {submission.evidence.photos.map(photo => <li key={photo.key} className="w-32" data-id="element-6316" data-mp="ouu4b23yuvq7">
                          {photo.dataUrl.startsWith('data:') ? <img src={photo.dataUrl} alt={photo.label} className="h-20 w-full rounded-md border border-line object-cover" data-id="element-6317" data-mp="ksz460xruoe0" /> : <div className="flex h-20 w-full items-center justify-center rounded-md border border-dashed border-line-strong bg-canvas text-[11px] text-ink-subtle" data-id="element-6318" data-mp="iwa92e7wkxj6">
                              Development sample
                            </div>}
                          <p className="mt-0.5 text-[11px] text-ink-subtle" data-id="element-6319" data-mp="dwe4odvan433">{photo.label}</p>
                        </li>)}
                    </ul>}

                  {submission.flags.length > 0 && <ul className="mt-2.5 space-y-1" data-id="element-6320" data-mp="47md46u5alsp">
                      {submission.flags.map(flag => <li key={flag} className="flex items-start gap-2 text-[12px] leading-5 text-warn-700" data-id="element-6321" data-mp="3md96rr49okg4">
                          <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-6322" data-mp="37qaho2svu24k" />
                          {flag}
                        </li>)}
                    </ul>}

                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-6323" data-mp="3gocee65l28m1">
                    <Button size="sm" variant="primary" onClick={() => setReviewId(submission.id)} data-id="element-6324" data-mp="3cnsgh0d6r0yu">
                      Decide
                    </Button>
                  </div>
                  <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-6325" data-mp="3lq1sjuo3r2ec">
                    Approving marks the property Campuzon Verified for six months and creates the
                    representative’s earning under the task’s reward rule. Nothing is paid on submission.
                  </p>
                </div>;
      })}
        </div>}

      {/* -------------------------------------------------------- finance -- */}
      {tab === 'Finance' && <div className="space-y-4" data-id="element-6326" data-mp="32354r0xv57hz">
          <Panel title="Earnings awaiting approval" description="Created by an approved submission under a rule that requires approval." data-id="element-6327" data-mp="3246hd9uj221t">
            {pendingEarnings.length === 0 ? <EmptyState title="Nothing pending" description="Approved work with no outstanding finance step." data-id="element-6328" data-mp="3b17296ie1i0i" /> : <ul className="divide-y divide-line" data-id="element-6329" data-mp="3ktd0uz35axoo">
                {pendingEarnings.map(entry => <li key={entry.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-6330" data-mp="3proc9e939g90">
                    <span className="min-w-0 flex-1" data-id="element-6331" data-mp="3uqr9icitui6f">
                      <span className="block text-[13px] font-medium text-ink" data-id="element-6332" data-mp="3up6d0hcycp5x">{entry.activity}</span>
                      <span className="cz-num block text-[12px] text-ink-muted" data-id="element-6333" data-mp="2lduph61bgvl">
                        {entry.id} · {network.reps.find(rep => rep.id === entry.repId)?.repCode} · rule{' '}
                        {network.rules.find(rule => rule.id === entry.ruleId)?.label}
                      </span>
                    </span>
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6334" data-mp="1s43q1y02s8do">
                      {naira(entry.grossAmount)}
                    </span>
                    <Button size="sm" variant="primary" onClick={() => network.approveEarning(entry.id, FINANCE)} data-id="element-6335" data-mp="1n5sjbw52udzo">
                      Approve
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => network.rejectEarning(entry.id, FINANCE, 'Outside the monthly cap for this rule')} data-id="element-6336" data-mp="2aw6azljr2atq">
                      Reject
                    </Button>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="Ledger" description="Append-only. Corrections create adjustment entries." data-id="element-6337" data-mp="qdyn4fm9t99b">
            <ul className="divide-y divide-line" data-id="element-6338" data-mp="2qg7xi4d4bdss">
              {network.earnings.map(entry => <li key={entry.id} className="py-3 first:pt-0" data-id="element-6339" data-mp="2uh074ypwjkpl">
                  <p className="flex flex-wrap items-baseline gap-2" data-id="element-6340" data-mp="2gkd9etq4kqim">
                    <span className="min-w-0 flex-1 text-[13px] text-ink" data-id="element-6341" data-mp="2li6hxtewhwm2">{entry.activity}</span>
                    <Badge tone={earningTone(entry.status)} data-id="element-6342" data-mp="39896hzjlwkfx">{entry.status}</Badge>
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6343" data-mp="39ad8gbxy39dk">
                      {naira(entry.grossAmount + entry.adjustment)}
                    </span>
                  </p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-6344" data-mp="2zgd2k7uua03s">
                    {entry.id} · {network.reps.find(rep => rep.id === entry.repId)?.repCode ?? entry.repId} ·{' '}
                    {entry.sourceRef} · created {entry.createdOn}
                    {entry.approvedBy ? ` · approved by ${entry.approvedBy}` : ''}
                    {entry.paidOn ? ` · paid ${entry.paidOn}` : ''}
                    {entry.reversalOf ? ` · reverses ${entry.reversalOf}` : ''}
                  </p>
                  {entry.status === 'Approved' && !entry.reversalOf && <Button size="sm" variant="ghost" className="mt-1" onClick={() => network.reverseEarning(entry.id, FINANCE, 'Reversed after the work was withdrawn')} data-id="element-6345" data-mp="1ud0rbjc6z212">
                      Reverse with an adjustment
                    </Button>}
                </li>)}
            </ul>
          </Panel>

          <Panel title="Payouts" data-id="element-6346" data-mp="16ng952nv48su">
            <ul className="divide-y divide-line" data-id="element-6347" data-mp="2mzbt2xbjb8zg">
              {network.reps.map(rep => {
            const balances = balancesFor(network.earnings.filter(entry => entry.repId === rep.id));
            if (balances.approved === 0) return null;
            return <li key={rep.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-6348" data-mp="2slhvjml7921c">
                    <span className="min-w-0 flex-1" data-id="element-6349" data-mp="2nmdp33pntiep">
                      <span className="block text-[13px] font-medium text-ink" data-id="element-6350" data-mp="2nn6plmy0zboe">{rep.name}</span>
                      <span className="cz-num block text-[12px] text-ink-muted" data-id="element-6351" data-mp="2iolvm59d9o8e">{rep.repCode}</span>
                    </span>
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6352" data-mp="3gd5ftlnyheun">
                      {naira(balances.approved)}
                    </span>
                    <Button size="sm" variant="primary" disabled={!network.flags.payouts} onClick={() => network.payoutApproved(rep.id, FINANCE)} data-id="element-6353" data-mp="36k7tf4fuf5qr">
                      {network.flags.payouts ? 'Release payout' : 'Payouts flag is off'}
                    </Button>
                  </li>;
          })}
            </ul>
            <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-6354" data-mp="5ip28lk51vav">
              Payouts settle through a licensed payment provider. Bank details are held by that provider,
              not in this console, and finance administrators cannot see them here.
            </p>
          </Panel>

          <Panel title="Disputes" data-id="element-6355" data-mp="kbs6jr8pycjn">
            {network.disputes.length === 0 ? <EmptyState title="No disputes" description="Representatives can dispute rejections, rewards and scores." data-id="element-6356" data-mp="oex5xkxi5y42" /> : <ul className="divide-y divide-line" data-id="element-6357" data-mp="y59udo8fubfh">
                {network.disputes.map(dispute => <li key={dispute.id} className="py-3 first:pt-0" data-id="element-6358" data-mp="13432t28vbc8s">
                    <p className="flex flex-wrap items-center gap-2" data-id="element-6359" data-mp="136fo9pdu44ql">
                      <span className="text-[13px] font-medium text-ink" data-id="element-6360" data-mp="1850mjj9j0ece">{dispute.about}</span>
                      <Badge tone={dispute.state === 'Upheld' ? 'ok' : dispute.state === 'Declined' ? 'danger' : 'warn'} data-id="element-6361" data-mp="1c5ub8euslx6c">
                        {dispute.state}
                      </Badge>
                      <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6362" data-mp="347nb3jfgxi5j">
                        {dispute.id} · {dispute.ref}
                      </span>
                    </p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-6363" data-mp="3d70lqffbwcru">{dispute.argument}</p>
                    {dispute.decision ? <p className="mt-1 text-[12px] leading-5 text-ink" data-id="element-6364" data-mp="3ry9gf2y38obu">Decision: {dispute.decision}</p> : <div className="mt-2 flex flex-wrap gap-2" data-id="element-6365" data-mp="4r70xodysf4z">
                        <Button size="sm" variant="primary" onClick={() => network.decideDispute(dispute.id, 'Upheld', 'Reviewer accepted the constraint described. Task reopened without penalty.', REVIEWER)} data-id="element-6366" data-mp="4pcqx548e7fo">
                          Uphold
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => network.decideDispute(dispute.id, 'Declined', 'Evidence still does not meet the published standard.', REVIEWER)} data-id="element-6367" data-mp="1imyvsxhut5ua">
                          Decline
                        </Button>
                      </div>}
                  </li>)}
              </ul>}
          </Panel>
        </div>}

      {/* ---------------------------------------------------- performance -- */}
      {tab === 'Performance' && <div className="space-y-4" data-id="element-6368" data-mp="u0nq70f5j168">
          <Panel title="Network performance" description="Scores are derived from reviewed work, never entered by hand." data-id="element-6369" data-mp="u1fkm7x0isux">
            <ul className="divide-y divide-line" data-id="element-6370" data-mp="ozhxh2lhrdwp">
              {network.reps.map(rep => {
            const breakdown = scoreFor(network.events.filter(item => item.repId === rep.id), network.verifications.filter(item => item.repId === rep.id), network.tasks.filter(item => item.assignedRepId === rep.id));
            const approved = network.verifications.filter(item => item.repId === rep.id && item.status === 'Approved').length;
            return <li key={rep.id} className="py-3 first:pt-0" data-id="element-6371" data-mp="3tezw2ceuh35z">
                    <p className="flex flex-wrap items-center gap-2" data-id="element-6372" data-mp="3og6fgb49gw1l">
                      <span className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-6373" data-mp="23ye3293212m2">{rep.name}</span>
                      <Badge tone={repTone(rep.status)} data-id="element-6374" data-mp="28xgzj54yy0if">{rep.rank}</Badge>
                      <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-6375" data-mp="28zlau85761sq">
                        {breakdown.score}/100
                      </span>
                    </p>
                    <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-6376" data-mp="2hxn76adzk69o">
                      {approved} approved verification(s)
                      {breakdown.improve.length > 0 ? ` · weak on ${breakdown.improve.join(', ')}` : ''}
                    </p>
                    {rep.status === 'Active' && <Button size="sm" variant="secondary" className="mt-1.5" disabled={!network.flags.leadership} onClick={() => network.promote(rep.id, ACTOR, 'Met the published promotion criteria')} data-id="element-6377" data-mp="1u79ovbl7dlsa">
                        Promote
                      </Button>}
                  </li>;
          })}
            </ul>
            <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-6378" data-mp="2imr4j1asfjpj">
              A promotion recorded here is an administrative decision and appears in the audit log with
              the reason given.
            </p>
          </Panel>
        </div>}

      {/* ------------------------------------------------ safety and fraud -- */}
      {tab === 'Safety and fraud' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-6379" data-mp="1zvht2mhgikvb">
          <Panel title="Fraud flags" description="An algorithmic flag is a prompt for review, not a verdict." data-id="element-6380" data-mp="1zxaw7rsv2myk">
            {openFraud.length === 0 ? <EmptyState title="Nothing awaiting review" description="Flags appear when a signal fires." data-id="element-6381" data-mp="3badeqseitmld" /> : <ul className="space-y-3" data-id="element-6382" data-mp="36b0hretxqz52">
                {openFraud.map(flag => <li key={flag.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-6383" data-mp="3eskkztpzop36">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-6384" data-mp="3iv79bkngbrvm">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-6385" data-mp="34v65pxfsy6cg">{flag.signal}</p>
                      <Badge tone={flag.severity === 'High' ? 'danger' : 'warn'} data-id="element-6386" data-mp="39thfk863kguo">{flag.severity}</Badge>
                      <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-6387" data-mp="1de9u7yjp28z">{flag.id}</span>
                    </div>
                    <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-6388" data-mp="6bzbmbtg3tzj">{flag.detail}</p>
                    <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-6389" data-mp="3nsh6xnkzv9uj">
                      {network.reps.find(rep => rep.id === flag.repId)?.repCode} · raised {flag.raisedOn}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-6390" data-mp="kbqv2s2qe2kx">
                      <Button size="sm" variant="secondary" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-6392" data-mp="2l76nzf08ccpm" />} onClick={() => network.resolveFraudFlag(flag.id, 'Cleared', ACTOR)} data-id="element-6391" data-mp="p38lim74okrn">
                        Clear
                      </Button>
                      <Button size="sm" variant="danger" icon={<XIcon className="h-3.5 w-3.5" data-id="element-6394" data-mp="27h32iq96cn18" />} onClick={() => network.resolveFraudFlag(flag.id, 'Upheld', ACTOR)} data-id="element-6393" data-mp="2bhd8i2wyvdg3">
                        Uphold
                      </Button>
                    </div>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="Incidents" description="Confidential reports withhold the reporter from leads." data-id="element-6395" data-mp="2q69ja7qer4ke">
            <ul className="divide-y divide-line" data-id="element-6396" data-mp="1jpxpsjyj6av5">
              {network.incidents.map(incident => <li key={incident.id} className="py-3 first:pt-0" data-id="element-6397" data-mp="1epjg6xha6sv0">
                  <p className="flex flex-wrap items-center gap-2" data-id="element-6398" data-mp="3qe6ga374y4q6">
                    <span className="text-[13px] font-medium text-ink" data-id="element-6399" data-mp="3qbtpd0jegk0z">{incident.kind}</span>
                    <Badge tone={incident.status === 'Resolved' ? 'ok' : 'warn'} data-id="element-6400" data-mp="3vd99mqdr6mib">{incident.status}</Badge>
                    {incident.confidential && <Badge tone="neutral" data-id="element-6401" data-mp="7zxalqxc7u29">Confidential</Badge>}
                  </p>
                  <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-6402" data-mp="81rv544p5k5t">{incident.detail}</p>
                  <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-6403" data-mp="d1e77a8sgud1">
                    {incident.confidential ? 'Reporter withheld' : network.reps.find(rep => rep.id === incident.repId)?.repCode}{' '}
                    · {incident.reportedOn}
                  </p>
                </li>)}
            </ul>
            <p className="mt-2.5 flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-6404" data-mp="2opvwbtl1knn9">
              <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-6405" data-mp="2sqxqhw03q9id" />
              Safety reports are never used as an input to a representative’s performance score.
            </p>
          </Panel>
        </div>}

      {/* --------------------------------------------------- intelligence -- */}
      {tab === 'Intelligence' && <Panel title="Intelligence queue" description="Nothing reaches a public campus page until it is verified and published." data-id="element-6406" data-mp="3hwkhln2vx936">
          <ul className="divide-y divide-line" data-id="element-6407" data-mp="2kavim0f4v7be">
            {network.intel.map(report => <li key={report.id} className="py-3 first:pt-0" data-id="element-6408" data-mp="2p8evavw6nc18">
                <p className="flex flex-wrap items-center gap-2" data-id="element-6409" data-mp="2u6yguv6v6lt2">
                  <Badge tone="neutral" data-id="element-6410" data-mp="2yxyds0fzh3vk">{report.category}</Badge>
                  <Badge tone={report.status === 'Published' || report.status === 'Verified' ? 'ok' : report.status === 'Rejected' ? 'danger' : 'warn'} data-id="element-6411" data-mp="2yzjcm29bz8z8">
                    {report.status}
                  </Badge>
                  <span className="ml-auto text-[12px] text-ink-subtle" data-id="element-6412" data-mp="1tzl4io768o50">
                    {report.confidence} confidence ·{' '}
                    {network.reps.find(rep => rep.id === report.repId)?.repCode}
                  </span>
                </p>
                <p className="mt-1 text-[13px] leading-6 text-ink" data-id="element-6413" data-mp="1fzsgj0n14m0q">{report.observation}</p>
                <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-6414" data-mp="1b8tstfzpolng">
                  {network.campuses.find(campus => campus.id === report.campusId)?.name} · observed{' '}
                  {report.observedOn}
                  {report.expiresOn ? ` · expires ${report.expiresOn}` : ' · no expiry'}
                  {report.hasEvidence ? ' · evidence attached' : ' · no evidence attached'}
                </p>
                {!['Published', 'Rejected'].includes(report.status) && <div className="mt-2 flex flex-wrap gap-2" data-id="element-6415" data-mp="hokp49ge0hby">
                    <Button size="sm" variant="primary" onClick={() => network.reviewIntel(report.id, 'Published', REVIEWER)} data-id="element-6416" data-mp="hn9htznqgrg3">
                      Verify and publish
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => network.reviewIntel(report.id, 'Rejected', REVIEWER)} data-id="element-6417" data-mp="dnsd58g4rybz">
                      Reject
                    </Button>
                  </div>}
              </li>)}
          </ul>
        </Panel>}

      {/* -------------------------------------------------- configuration -- */}
      {tab === 'Configuration' && <div className="space-y-4" data-id="element-6418" data-mp="1kanwjf9njv2a">
          <Panel title="Reward rules" description="Amounts, caps and approval requirements are configuration, not code." data-id="element-6419" data-mp="1k9l2wzky3eio">
            <ul className="divide-y divide-line" data-id="element-6420" data-mp="1yfn2ny9dma66">
              {network.rules.map(rule => <li key={rule.id} className="py-3 first:pt-0" data-id="element-6421" data-mp="1uel1hbvwfo1k">
                  <p className="flex flex-wrap items-center gap-2" data-id="element-6422" data-mp="286ho50est5qk">
                    <span className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-6423" data-mp="288vnhlgih43x">{rule.label}</span>
                    <Badge tone={rule.active ? 'ok' : 'neutral'} data-id="element-6424" data-mp="23a0szm96lr3z">{rule.active ? 'Active' : 'Paused'}</Badge>
                    <Badge tone="neutral" data-id="element-6425" data-mp="1bgi8km08d6yh">{rule.kind}</Badge>
                  </p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-6426" data-mp="1bil9w26u2mii">
                    {rule.percentage !== undefined ? `${rule.percentage}% commission` : naira(rule.amount)} ·{' '}
                    {rule.campusId ? 'campus-specific' : 'all campuses'} ·{' '}
                    {rule.requiresApproval ? 'approval required' : 'auto-approved'} ·{' '}
                    {rule.monthlyCap ? `cap ${naira(rule.monthlyCap)}/month` : 'no cap'} · from{' '}
                    {rule.effectiveFrom}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-6427" data-mp="1ae2xojkxwwd8">
                    <Button size="sm" variant="secondary" onClick={() => network.updateRule(rule.id, {
                active: !rule.active
              }, ACTOR)} data-id="element-6428" data-mp="1ag5yzzrjmbx9">
                      {rule.active ? 'Pause' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => network.updateRule(rule.id, {
                requiresApproval: !rule.requiresApproval
              }, ACTOR)} data-id="element-6429" data-mp="1td75xq4nqm3u">
                      {rule.requiresApproval ? 'Remove approval step' : 'Require approval'}
                    </Button>
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Feature flags" description="Controlled rollout. Every change is audited." data-id="element-6430" data-mp="3rmmja5ar4c8h">
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-6431" data-mp="3nkrg0v99fos2">
              {(Object.keys(network.flags) as FeatureFlagKey[]).map(key => <li key={key} className="flex items-center gap-2 rounded-lg border border-line-strong bg-canvas px-3.5 py-2.5" data-id="element-6432" data-mp="3imzm5vp21sl8">
                  <span className="cz-num min-w-0 flex-1 text-[12px] text-ink" data-id="element-6433" data-mp="f4zcmc3upf5i">{key}</span>
                  <Badge tone={network.flags[key] ? 'ok' : 'neutral'} data-id="element-6434" data-mp="adzfp6uqex30">
                    {network.flags[key] ? 'On' : 'Off'}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => network.toggleFlag(key, ACTOR)} data-id="element-6435" data-mp="5f63riv3fe4y">
                    Toggle
                  </Button>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Verification requirements" data-id="element-6436" data-mp="1h0c462rnmuv0">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-6437" data-mp="1h1n7k1h6u0ll">
              Each task type carries its own evidence set, and a representative cannot submit until every
              required item is captured. The location radius is currently{' '}
              <span className="cz-num font-semibold text-ink" data-id="element-6438" data-mp="1c9lx8l3kyjy7">{VERIFICATION_RADIUS_M}m</span> — submissions
              outside it are flagged for review rather than rejected outright.
            </p>
          </Panel>
        </div>}

      {/* ---------------------------------------------------------- audit -- */}
      {tab === 'Audit' && <Panel title="Audit log" description="Immutable. Who, what, from, to, and why." data-id="element-6439" data-mp="paqv31z1hxyu">
          <ul className="divide-y divide-line" data-id="element-6440" data-mp="tbj5hyjne8wp">
            {network.audit.map(entry => <li key={entry.id} className="py-3 first:pt-0" data-id="element-6441" data-mp="2jo8nb2ae3j07">
                <p className="text-[13px] font-medium text-ink" data-id="element-6442" data-mp="2noirc9emfzcf">{entry.action}</p>
                <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-6443" data-mp="2nmg1pqhds888">
                  {entry.at} · {entry.actor} · {entry.entity}
                  {entry.from ? ` · from ${entry.from}` : ''}
                  {entry.to ? ` → ${entry.to}` : ''}
                </p>
                {entry.reason && <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-6444" data-mp="h0ieik9yretn">Reason: {entry.reason}</p>}
              </li>)}
          </ul>
        </Panel>}

      <ApproveDialog applicationId={approveId} onClose={() => setApproveId(null)} data-id="element-6445" data-mp="vr96bsbk2dwr" />
      <ReviewDialog submissionId={reviewId} onClose={() => setReviewId(null)} data-id="element-6446" data-mp="14q4cx5iihq0l" />
    </div>;
}
function ApproveDialog({
  applicationId,
  onClose
}: {
  applicationId: string | null;
  onClose: () => void;
}) {
  const network = useNetwork();
  const application = network.applications.find(item => item.id === applicationId) ?? null;
  const [campusId, setCampusId] = useState(application?.campusId ?? '');
  const [areaId, setAreaId] = useState('');
  const [rank, setRank] = useState<RepRank>('Campus Representative');
  const [supervisor, setSupervisor] = useState('');
  const [reason, setReason] = useState('');
  if (!application) return null;
  const campus = network.campuses.find(item => item.id === (campusId || application.campusId));
  return <Modal open={applicationId !== null} onClose={onClose} title={`Approve ${application.fullName}`} description="Assignment and rank are set here. A reason is required and is written to the audit log." data-id="element-6447" data-mp="2ucscxib4w4v9">
      <div className="space-y-3" data-id="element-6448" data-mp="21r975g9avxno">
        <Field label="Campus" data-id="element-6449" data-mp="26nzjptlwcb4c">
          <Select value={campusId || application.campusId} onChange={event => {
          setCampusId(event.target.value);
          setAreaId('');
        }} data-id="element-6450" data-mp="26mxywxf2wa9f">
            {network.campuses.map(item => <option key={item.id} value={item.id} data-id="element-6451" data-mp="3n2itrz45hkv2">
                {item.name}
              </option>)}
          </Select>
        </Field>
        <Field label="Area" hint="Optional at appointment." data-id="element-6452" data-mp="1bennpzs8vyo2">
          <Select value={areaId} onChange={event => setAreaId(event.target.value)} data-id="element-6453" data-mp="16g2l5tpit2wg">
            <option value="" data-id="element-6454" data-mp="11p43oqsspyrm">Not assigned yet</option>
            {campus?.areas.map(area => <option key={area.id} value={area.id} data-id="element-6455" data-mp="wr2m6ab0q7i9">
                {area.name}
              </option>)}
          </Select>
        </Field>
        <Field label="Rank" data-id="element-6456" data-mp="sq8xhepr4oob">
          <Select value={rank} onChange={event => setRank(event.target.value as RepRank)} data-id="element-6457" data-mp="npupfwo5heqa">
            {RANK_ORDER.map(item => <option key={item} data-id="element-6458" data-mp="irt578ezn9d8">{item}</option>)}
          </Select>
        </Field>
        <Field label="Supervisor" data-id="element-6459" data-mp="dsgluxnbskfe">
          <Select value={supervisor} onChange={event => setSupervisor(event.target.value)} data-id="element-6460" data-mp="dthu6ucyistf">
            <option value="" data-id="element-6461" data-mp="2d3zt97szdkmy">No supervisor</option>
            {network.reps.filter(rep => rep.campusId === (campusId || application.campusId)).map(rep => <option key={rep.id} value={rep.id} data-id="element-6462" data-mp="2zuj1c9vhvis7">
                  {rep.name} — {rep.rank}
                </option>)}
          </Select>
        </Field>
        <Field label="Reason for this decision" data-id="element-6463" data-mp="39m6nbv9a87ma">
          <Textarea rows={2} value={reason} onChange={event => setReason(event.target.value)} data-id="element-6464" data-mp="39nzsf648yjs6" />
        </Field>
        <p className="rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-6465" data-mp="34p6i1mk9b8w8">
          Approval creates an account in Pending onboarding. No tasks or earnings are available until the
          representative completes the agreement, conduct, privacy, identity and training steps.
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-6466" data-mp="2lw2v0k1jnual">
          <Button variant="primary" disabled={reason.trim().length < 8} onClick={() => {
          network.approveApplication(application.id, {
            campusId: campusId || application.campusId,
            areaId: areaId || null,
            rank,
            supervisorRepId: supervisor || null,
            reason: reason.trim()
          });
          onClose();
        }} data-id="element-6467" data-mp="2lv19flmwjpem">
            Approve and create account
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-6468" data-mp="3qjm2k90m913">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>;
}
function ReviewDialog({
  submissionId,
  onClose
}: {
  submissionId: string | null;
  onClose: () => void;
}) {
  const network = useNetwork();
  const submission = network.verifications.find(item => item.id === submissionId) ?? null;
  const [decision, setDecision] = useState<VerificationDecision>('Approved');
  const [notes, setNotes] = useState('');
  if (!submission) return null;
  return <Modal open={submissionId !== null} onClose={onClose} title={`Review ${submission.id}`} description="Your decision changes what students see and whether an earning exists." data-id="element-6469" data-mp="1aeq46ch4knbn">
      <div className="space-y-3" data-id="element-6470" data-mp="1p0nky42vox96">
        <Field label="Decision" data-id="element-6471" data-mp="1ozktnv1n8ssq">
          <Select value={decision} onChange={event => setDecision(event.target.value as VerificationDecision)} data-id="element-6472" data-mp="1l0d4tyldgqgl">
            <option data-id="element-6473" data-mp="1fzfm9kwg9uek">Approved</option>
            <option data-id="element-6474" data-mp="1b1w7mjvubfm3">Rework required</option>
            <option data-id="element-6475" data-mp="1b14c1928thvt">Rejected</option>
          </Select>
        </Field>
        <Field label="Notes for the representative" hint="Required. They see this, and can dispute it." data-id="element-6476" data-mp="163ckwh9f9zso">
          <Textarea rows={3} value={notes} onChange={event => setNotes(event.target.value)} data-id="element-6477" data-mp="11bb2sepkpeuq" />
        </Field>
        {submission.flags.length > 0 && <div className="rounded-lg border border-warn-100 bg-warn-50 px-3.5 py-2.5" data-id="element-6478" data-mp="sbo96f335dc8">
            <p className="text-[12px] font-semibold text-warn-700" data-id="element-6479" data-mp="ncbnvyrv4ebr">
              {submission.flags.length} flag(s) on this submission
            </p>
            <ul className="mt-1 space-y-0.5" data-id="element-6480" data-mp="iejjw6dy11r7">
              {submission.flags.map(flag => <li key={flag} className="text-[12px] leading-5 text-warn-700" data-id="element-6481" data-mp="88ib1rtss9rn">
                  · {flag}
                </li>)}
            </ul>
          </div>}
        <p className="rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-6482" data-mp="i77w1tgzqac4">
          {decision === 'Approved' ? 'Approving grants Campuzon Verified for six months and creates the earning under the task’s reward rule.' : decision === 'Rework required' ? 'Rework returns the task to the representative with your notes. No earning is created.' : 'Rejection closes the task with no earning. The representative may dispute it.'}
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-6483" data-mp="499i2p6l0fx5">
          <Button variant="primary" disabled={notes.trim().length < 8} onClick={() => {
          network.reviewVerification(submission.id, decision, notes.trim(), REVIEWER);
          onClose();
        }} data-id="element-6484" data-mp="32xyj837bs4iu">
            Record decision
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-6485" data-mp="18i045hchn0xe">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>;
}